import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  MutableRefObject,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Space, Tag, Tooltip } from "antd";

import { isEqual, omit } from "lodash";
import FilterIcon, { IFilterInfo } from "@lhd/components/FilterIcon";
import { IOptions, LogTimeByDateResponse } from "@lhd/types";
import {
  WorkerLogtimesSaveJsonItem,
  WorkerLogtimeMonthlyV2Record,
  fetchWorkerLogTimesByDate,
  getWorkerLogtimeApiErrorMessage,
  saveWorkerLogtimes,
  useFetchWorkerLogtimesFilterOptionsV2,
} from "@lhd/hooks";
import {
  BUTTON_HEIGHT,
  DATE_TIME_FORMAT,
  FONT_SIZE,
  ROW_HEIGHT_E_LEARNING,
} from "@lhd/constants";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import { DISPLAY_SIZES } from "@lhd/types";
import moment from "moment";
import { useManagerTrackingPath } from "../../useManagerTrackingPath";
import {
  BLANK_FILTER_LABEL,
  MONTHLY_LIST_SORT_API_FIELDS,
  MONTHLY_LIST_URL,
  applyMonthlyListPatch,
  isNullFilterToken,
  formatApiDateTimeForFilterDisplay,
  jpDateTimeStringToJpCalendarDay,
  mapLogtimeStatusFilterToApi,
  monthlyListFilterBracketKey,
  monthlyListFiltersSignature,
  monthlyListSearchBracketKey,
  monthlyListSortBracketKey,
  normalizeJapaneseDateInput,
  parseBracketFiltersFromSearchParams,
  parseBracketSearchesFromSearchParams,
  parseBracketSortsFromSearchParams,
  type MonthlyListSearchPatch,
} from "../monthlyListQuery";
import { TRACKING_LIST_AG_LOCALE } from "./agGridTrackingLocale";
import {
  CHECK_IN_HOUR_COUNT,
  EXTENDED_TIME_HOUR_COUNT,
  getTrackingStatusChipColors,
  getTrackingStatusLabel,
  TRACKING_TIME_STATUS,
} from "@lhd/features/TrackingTime/until";
import { useTrackingViewportHeight } from "../../useTrackingViewportHeight";
import ButtonCommon from "@lhd/components/ButtonCommon";
import { TYPE_INPUT } from "@lhd/components/TableForm/types";
import type {
  FilterableColumnKey,
  ReceivedProps,
  TimeCellRendererParams,
  TrackingAgRow,
  TrackingFilterState,
} from "./type";
import {
  getColumnFromV2FilterField,
  getOriginalCellData,
  getTrackingDisabledTimeData,
  getV2FilterFieldFromColumn,
  getWorkerLogtimeDisplayTime,
} from "./utils";
import TextToolTip from "@lhd/components/TextToolTip";
import InlineAttendanceEditor from "./InlineAttendanceEditor";
import CellHistory from "./CellHistory";
import { useAttendanceManagementPermission } from "../../useAttendanceManagementPermission";
import { textAlign } from "@mui/system";

export type { ReceivedProps, TrackingAgRow } from "./type";

const TRACKING_LIST_TABLE_HEIGHT_OFFSET = 260;

const FILTERABLE_COLUMN_KEYS = [
  "employeeName",
  "date",
  "startAt",
  "endAt",
  "correctedCheckIn",
  "correctedCheckOut",
  "memo",
  "workingTime",
  "breakTime",
  "status",
  "creator",
  "createdAt",
] as const;

const toFilterOptions = (values: string[]): IOptions => {
  const uniq = Array.from(new Set(values.map((v) => String(v ?? ""))));
  return uniq.map((value) => ({
    value,
    label: isNullFilterToken(value) ? BLANK_FILTER_LABEL : value,
  }));
};

const normalizeTextFilterValues = (
  values: Array<string | null | undefined>,
): string[] => values.map((v) => String(v ?? "").trim()).filter(Boolean);

const splitCsvParam = (raw: string | null): string[] =>
  (raw ?? "")
    .split(/[,，]/)
    .map((s) => s.trim())
    .filter(Boolean);

const uniqValues = (values: string[]): string[] => Array.from(new Set(values));
const stripNullFilterTokens = (values: string[]): string[] =>
  values.filter((v) => !isNullFilterToken(v));
const extractNullFilterTokens = (values: string[]): string[] =>
  uniqValues(values.filter(isNullFilterToken));

const toCreatedAtFilterOptionApiValue = (raw: string): string => {
  const value = normalizeJapaneseDateInput(raw);
  if (!value) return value;
  const yearOnlyFormats = [DATE_TIME_FORMAT.YEAR_JP, "YYYY"];
  for (const fmt of yearOnlyFormats) {
    const m = moment(value, fmt, true);
    if (m.isValid()) {
      return m.format("YYYY");
    }
  }
  const monthOnlyApi = moment(value, "YYYY-MM", true);
  if (monthOnlyApi.isValid()) {
    return monthOnlyApi.format("YYYY-MM");
  }
  const withTimeFormats = [
    DATE_TIME_FORMAT.DATE_TIME_JP,
    DATE_TIME_FORMAT.DATE_JP_TIME,
    DATE_TIME_FORMAT.DATE_TIME_JP_MD,
    DATE_TIME_FORMAT.DATE_JP_MD_TIME,
    "YYYY年MM月DD日 HH",
    "YYYY年M月D日 H",
    "YYYY年MM月DD日HH",
    "YYYY年M月D日H",
  ];
  for (const fmt of withTimeFormats) {
    const m = moment(value, fmt, true);
    if (m.isValid()) {
      return m.format("YYYY-MM-DD HH:mm:ss");
    }
  }
  const dateOnlyFormats = [
    DATE_TIME_FORMAT.DATE_JP,
    DATE_TIME_FORMAT.DATE_JP_MD,
  ];
  for (const fmt of dateOnlyFormats) {
    const m = moment(value, fmt, true);
    if (m.isValid()) {
      return m.format("YYYY-MM-DD");
    }
  }
  const monthOnlyFormats = [
    DATE_TIME_FORMAT.DATE_MONTH_JP,
    DATE_TIME_FORMAT.DATE_MONTH_JP_M,
  ];
  for (const fmt of monthOnlyFormats) {
    const m = moment(value, fmt, true);
    if (m.isValid()) {
      return m.format("YYYY-MM");
    }
  }
  const dateOnlyApi = moment(value, "YYYY-MM-DD", true);
  if (dateOnlyApi.isValid()) {
    return dateOnlyApi.format("YYYY-MM-DD");
  }
  const t = value.trim();
  if (/^\d{4}(-\d{2}){0,2}$/.test(t)) {
    return t;
  }
  return value;
};

const normalizeCreatedAtActiveFilterValue = (raw: string): string => {
  const trimmed = raw.trim();
  if (isNullFilterToken(trimmed)) return trimmed;
  const v = toCreatedAtFilterOptionApiValue(raw).trim();
  if (!v) return v;
  if (/^\d{4}(-\d{2}){0,2}$/.test(v)) return v;
  const m = moment.parseZone(
    v,
    ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", moment.ISO_8601],
    true,
  );
  if (m.isValid()) return m.format("YYYY-MM-DD");
  return v;
};

const filterSearchApiTokenToDisplayLabel = (api: string): string =>
  apiDateTokenToJpCalendarDayLabel(api);

const apiDateTokenToJpCalendarDayLabel = (api: string): string => api.trim();

const toCheckboxFilter = (
  values: string[],
): Record<string, boolean> | undefined => {
  const cleaned = values.map((v) => String(v ?? "").trim()).filter(Boolean);
  if (!cleaned.length) return undefined;
  if (cleaned.length === 1 && cleaned[0] === "__empty_filter__") return {};
  return cleaned.reduce((acc, v) => {
    acc[v] = true;
    return acc;
  }, {} as Record<string, boolean>);
};

const matchFilterOptionValuesBySearch = (
  search: string,
  options: Array<{ value?: string | number; label?: string | number }> = [],
): string[] => {
  const keyword = normalizeJapaneseDateInput(search).trim().toLowerCase();
  if (!keyword) return [];

  return uniqValues(
    options
      .filter((option) => {
        const value = normalizeJapaneseDateInput(
          String(option.value ?? ""),
        ).toLowerCase();
        const label = normalizeJapaneseDateInput(
          String(option.label ?? ""),
        ).toLowerCase();
        return value.includes(keyword) || label.includes(keyword);
      })
      .map((option) => String(option?.value ?? "").trim())
      .filter(Boolean),
  );
};

const statusLabelFromUrlToken = (token: string): string => {
  const t = token.trim();
  if (!t) return t;
  const jp = TRACKING_TIME_STATUS[t as keyof typeof TRACKING_TIME_STATUS];
  if (jp) return jp;
  return t;
};

const API_SORT_FIELD_TO_COLUMN: Record<string, FilterableColumnKey> = {
  worker_name: "employeeName",
  punch_check_in: "startAt",
  punch_check_out: "endAt",
  corrected_check_in: "correctedCheckIn",
  corrected_check_out: "correctedCheckOut",
  last_checkout_time: "endAt",
  first_checkin_time: "startAt",
  note: "memo",
  logtime_status: "status",
  creator_name: "creator",
  created_at: "createdAt",
};

const buildTrackingFilterStateFromUrl = (
  sp: URLSearchParams,
): TrackingFilterState => {
  const out: TrackingFilterState = {};

  const workerCont =
    sp.get(MONTHLY_LIST_URL.workerNameCont)?.trim() ||
    sp.get("worker_name_cont")?.trim();
  const workerAny = splitCsvParam(
    sp.get(MONTHLY_LIST_URL.workerNameContAny) ??
      sp.get("worker_name_cont_any"),
  );
  const workerCb = toCheckboxFilter(workerAny);
  if (workerCont || workerCb) {
    out.employeeName = {
      ...(workerCont ? { search: workerCont } : {}),
      ...(workerCb ? { filter: workerCb } : {}),
    };
  }

  const creatorCont =
    sp.get(MONTHLY_LIST_URL.creatorNameCont)?.trim() ||
    sp.get("creator_name_cont")?.trim();
  const creatorAny = splitCsvParam(
    sp.get(MONTHLY_LIST_URL.creatorNameContAny) ??
      sp.get("creator_name_cont_any"),
  );
  const creatorCb = toCheckboxFilter(creatorAny);
  if (creatorCont || creatorCb) {
    out.creator = {
      ...(creatorCont ? { search: creatorCont } : {}),
      ...(creatorCb ? { filter: creatorCb } : {}),
    };
  }

  const noteCont =
    sp.get(MONTHLY_LIST_URL.noteCont)?.trim() || sp.get("note_cont")?.trim();
  const noteAny = splitCsvParam(
    sp.get(MONTHLY_LIST_URL.noteContAny) ?? sp.get("note_cont_any"),
  );
  const noteCb = toCheckboxFilter(noteAny);
  if (noteCont || noteCb) {
    out.memo = {
      ...(noteCont ? { search: noteCont } : {}),
      ...(noteCb ? { filter: noteCb } : {}),
    };
  }

  const statusSearch = sp.get(MONTHLY_LIST_URL.logtimeStatus)?.trim();
  if (statusSearch) {
    out.status = { ...(out.status || {}), search: statusSearch };
  }
  const statusAnyTokens = splitCsvParam(
    sp.get(MONTHLY_LIST_URL.logtimeStatusAny),
  );
  if (statusAnyTokens.length) {
    const nullTokens = extractNullFilterTokens(statusAnyTokens);
    const nonEmptyTokens = stripNullFilterTokens(statusAnyTokens);
    const labels = uniqValues(
      nonEmptyTokens.map((token) => statusLabelFromUrlToken(token)),
    );
    const filt = toCheckboxFilter([...nullTokens, ...labels]);
    out.status = {
      ...(out.status || {}),
      ...(filt ? { filter: filt } : {}),
    };
  }

  const createdAtAnyApi = splitCsvParam(sp.get(MONTHLY_LIST_URL.createdAtAny));
  const filterCreatedAt = sp.get(MONTHLY_LIST_URL.filterCreatedAt)?.trim();
  if (createdAtAnyApi.length) {
    const nullTokens = extractNullFilterTokens(createdAtAnyApi);
    const nonTokenCreatedAt = stripNullFilterTokens(createdAtAnyApi);
    const labels = uniqValues(
      nonTokenCreatedAt
        .map((api) => {
          const rawApi = api.trim();
          const y = moment(rawApi, "YYYY", true);
          if (y.isValid()) return y.format(DATE_TIME_FORMAT.YEAR_JP);
          const ym = moment(rawApi, "YYYY-MM", true);
          if (ym.isValid()) return ym.format(DATE_TIME_FORMAT.DATE_MONTH_JP);
          const d = moment(rawApi, "YYYY-MM-DD", true);
          if (d.isValid()) return d.format(DATE_TIME_FORMAT.DATE_JP);
          return jpDateTimeStringToJpCalendarDay(rawApi);
        })
        .filter((x): x is string => Boolean(x)),
    );
    const filt = toCheckboxFilter([...nullTokens, ...labels]);
    if (filt) {
      out.createdAt = {
        ...(out.createdAt || {}),
        filter: filt,
      };
    }
  }
  const filterFirstCheckin = sp
    .get(MONTHLY_LIST_URL.filterFirstCheckinTime)
    ?.trim();
  const filterLastCheckout = sp
    .get(MONTHLY_LIST_URL.filterLastCheckoutTime)
    ?.trim();

  if (filterCreatedAt) {
    out.createdAt = {
      ...(out.createdAt || {}),
      search: filterSearchApiTokenToDisplayLabel(filterCreatedAt),
    };
  }
  if (!createdAtAnyApi.length && !filterCreatedAt) {
    const caGteq = trimOrFirst(sp.get(MONTHLY_LIST_URL.createdAtGteq));
    const caLteq = trimOrFirst(sp.get(MONTHLY_LIST_URL.createdAtLteq));
    if (caGteq || caLteq) {
      const mg = moment.parseZone(caGteq || "");
      const ml = moment.parseZone(caLteq || "");
      let searchStr: string | undefined;
      if (
        mg.isValid() &&
        ml.isValid() &&
        mg.format("YYYY-MM-DD") === ml.format("YYYY-MM-DD")
      ) {
        searchStr = mg.format(DATE_TIME_FORMAT.DATE_JP);
      } else if (caGteq) {
        searchStr = formatApiDateTimeForFilterDisplay(caGteq);
      }
      if (searchStr) {
        out.createdAt = { ...(out.createdAt || {}), search: searchStr };
      }
    } else {
      const dateIn = splitCsvParam(sp.get(MONTHLY_LIST_URL.dateIn));
      const createdAtFromRange = formatApiDateTimeForFilterDisplay(
        trimOrFirst(sp.get(MONTHLY_LIST_URL.createdAtGteq)),
      );
      const createdAtSelected = uniqValues([
        ...dateIn
          .map((x) => jpDateTimeStringToJpCalendarDay(x) ?? x)
          .filter((x): x is string => Boolean(x)),
        ...(createdAtFromRange
          ? [jpDateTimeStringToJpCalendarDay(createdAtFromRange)].filter(
              (x): x is string => Boolean(x),
            )
          : []),
      ]);
      if (createdAtSelected.length) {
        const filt = toCheckboxFilter(createdAtSelected);
        if (filt) out.createdAt = { ...(out.createdAt || {}), filter: filt };
      }
    }
  }

  const firstCheckinAnyApi = splitCsvParam(
    sp.get(MONTHLY_LIST_URL.firstCheckinTimeAny),
  );
  if (firstCheckinAnyApi.length) {
    const nullTokens = extractNullFilterTokens(firstCheckinAnyApi);
    const nonToken = stripNullFilterTokens(firstCheckinAnyApi);
    const labels = uniqValues(
      nonToken
        .map((api) => apiDateTokenToJpCalendarDayLabel(api))
        .filter((x): x is string => Boolean(x)),
    );
    const filt = toCheckboxFilter([...nullTokens, ...labels]);
    if (filt) {
      out.startAt = {
        ...(out.startAt || {}),
        filter: filt,
      };
    }
  }
  if (filterFirstCheckin) {
    out.startAt = {
      ...(out.startAt || {}),
      search: filterSearchApiTokenToDisplayLabel(filterFirstCheckin),
    };
  }
  if (!firstCheckinAnyApi.length && !filterFirstCheckin) {
    const fiGteq = trimOrFirst(sp.get(MONTHLY_LIST_URL.firstCheckinGteq));
    const fiLteq = trimOrFirst(sp.get(MONTHLY_LIST_URL.firstCheckinLteq));
    if (fiGteq || fiLteq) {
      const mg = moment.parseZone(fiGteq || "");
      const ml = moment.parseZone(fiLteq || "");
      let searchStr: string | undefined;
      if (
        mg.isValid() &&
        ml.isValid() &&
        mg.format("YYYY-MM-DD") === ml.format("YYYY-MM-DD")
      ) {
        searchStr = mg.format(DATE_TIME_FORMAT.DATE_JP);
      } else if (fiGteq) {
        searchStr = formatApiDateTimeForFilterDisplay(fiGteq);
      }
      if (searchStr) {
        out.startAt = { ...(out.startAt || {}), search: searchStr };
      }
    }
  }

  const lastCheckoutAnyApi = splitCsvParam(
    sp.get(MONTHLY_LIST_URL.lastCheckoutTimeAny),
  );
  if (lastCheckoutAnyApi.length) {
    const nullTokens = extractNullFilterTokens(lastCheckoutAnyApi);
    const nonToken = stripNullFilterTokens(lastCheckoutAnyApi);
    const labels = uniqValues(
      nonToken
        .map((api) => apiDateTokenToJpCalendarDayLabel(api))
        .filter((x): x is string => Boolean(x)),
    );
    const filt = toCheckboxFilter([...nullTokens, ...labels]);
    if (filt) {
      out.endAt = {
        ...(out.endAt || {}),
        filter: filt,
      };
    }
  }
  if (filterLastCheckout) {
    out.endAt = {
      ...(out.endAt || {}),
      search: filterSearchApiTokenToDisplayLabel(filterLastCheckout),
    };
  }
  if (!lastCheckoutAnyApi.length && !filterLastCheckout) {
    const loGteq = trimOrFirst(sp.get(MONTHLY_LIST_URL.lastCheckoutGteq));
    const loLteq = trimOrFirst(sp.get(MONTHLY_LIST_URL.lastCheckoutLteq));
    if (loGteq || loLteq) {
      const mg = moment.parseZone(loGteq || "");
      const ml = moment.parseZone(loLteq || "");
      let searchStr: string | undefined;
      if (
        mg.isValid() &&
        ml.isValid() &&
        mg.format("YYYY-MM-DD") === ml.format("YYYY-MM-DD")
      ) {
        searchStr = mg.format(DATE_TIME_FORMAT.DATE_JP);
      } else if (loGteq) {
        searchStr = formatApiDateTimeForFilterDisplay(loGteq);
      }
      if (searchStr) {
        out.endAt = { ...(out.endAt || {}), search: searchStr };
      }
    }
  }

  const v2Filters = parseBracketFiltersFromSearchParams(sp);
  for (const [apiField, values] of Object.entries(v2Filters)) {
    const columnId = getColumnFromV2FilterField(apiField);
    if (!columnId) continue;

    const displayValues =
      columnId === "status"
        ? values.map((value) => statusLabelFromUrlToken(value))
        : values;
    const selectedFilter = toCheckboxFilter(displayValues);
    if (selectedFilter) {
      out[columnId] = {
        ...(out[columnId] || {}),
        filter: selectedFilter,
      };
    }
  }

  const v2Searches = parseBracketSearchesFromSearchParams(sp);
  for (const [apiField, search] of Object.entries(v2Searches)) {
    const columnId = getColumnFromV2FilterField(apiField);
    if (!columnId) continue;
    out[columnId] = { ...(out[columnId] || {}), search };
  }

  let sorts = parseBracketSortsFromSearchParams(sp);
  if (Object.keys(sorts).length === 0) {
    const sortBy = sp.get(MONTHLY_LIST_URL.sortBy)?.trim();
    const sortOrderRaw = sp.get(MONTHLY_LIST_URL.sortOrder)?.trim();
    const sortOrder =
      sortOrderRaw === "asc" || sortOrderRaw === "desc"
        ? sortOrderRaw
        : undefined;
    if (sortBy && sortOrder) sorts = { [sortBy]: sortOrder };
  }
  for (const [apiField, order] of Object.entries(sorts)) {
    const col =
      API_SORT_FIELD_TO_COLUMN[apiField] ||
      getColumnFromV2FilterField(apiField);
    if (col) {
      out[col] = { ...(out[col] || {}), sort: order };
    }
  }
  return out;
};

const buildPatchFromPanelFilter = (
  f: TrackingFilterState,
  fallbackFilterOptions?: Partial<Record<FilterableColumnKey, IOptions>>,
  urlSearchParams?: URLSearchParams,
): MonthlyListSearchPatch => {
  const readChecked = (k: FilterableColumnKey): string[] =>
    Object.keys(f[k]?.filter || {}).filter(Boolean);
  const toCsvOrNull = (
    arr: string[],
    colKey: FilterableColumnKey,
  ): string | null => {
    const colState = f[colKey];
    if (colState && colState.filter && typeof colState.filter === "object") {
      const hasChecked = Object.values(colState.filter).some((v) => v === true);
      if (
        !hasChecked &&
        !colState.search?.trim() &&
        !colState.filter_special_empty
      ) {
        return "__empty_filter__";
      }
    }
    return arr.length ? arr.join(",") : null;
  };

  let hasExplicitEmpty = false;
  let hasAnyActiveFilter = false;

  for (const col of Object.keys(f)) {
    const colState = f[col as FilterableColumnKey];

    // Check if this column has any actual filter/search/special
    const hasSearch = !!colState?.search?.trim();
    const hasFilter =
      colState?.filter &&
      typeof colState.filter === "object" &&
      Object.values(colState.filter).some((v) => v === true);
    const hasSpecial = !!colState?.filter_special_empty;

    if (hasSearch || hasFilter || hasSpecial) {
      hasAnyActiveFilter = true;
    }

    // Check for explicit empty filter state
    if (colState && colState.filter && typeof colState.filter === "object") {
      const hasChecked = Object.values(colState.filter).some((v) => v === true);
      if (
        !hasChecked &&
        !colState.search?.trim() &&
        !colState.filter_special_empty
      ) {
        hasExplicitEmpty = true;
      }
    }
  }

  const patch: MonthlyListSearchPatch = {
    empty_filter: hasExplicitEmpty ? "1" : null,
    [MONTHLY_LIST_URL.workerNameCont]: null,
    filter_dates: null,
    filter_company_names: null,
    [MONTHLY_LIST_URL.workerNameContAny]: toCsvOrNull(
      readChecked("employeeName"),
      "employeeName",
    ),
    [MONTHLY_LIST_URL.noteCont]: null,
    [MONTHLY_LIST_URL.noteContAny]: toCsvOrNull(readChecked("memo"), "memo"),
    [MONTHLY_LIST_URL.creatorNameCont]: null,
    [MONTHLY_LIST_URL.creatorNameContAny]: toCsvOrNull(
      readChecked("creator"),
      "creator",
    ),
    [MONTHLY_LIST_URL.logtimeStatus]: null,
    [MONTHLY_LIST_URL.logtimeStatusAny]: toCsvOrNull(
      readChecked("status")
        .map((k) => mapLogtimeStatusFilterToApi(k) ?? k)
        .filter(Boolean),
      "status",
    ),
    [MONTHLY_LIST_URL.dateIn]: null,
  };

  const SORT_FIELD_MAP: Record<string, string> = {
    employeeName: "worker_name",
    startAt: "punch_check_in", // 打刻 出勤
    endAt: "punch_check_out", // 打刻 退勤
    correctedCheckIn: "corrected_check_in", // 修正 出勤
    correctedCheckOut: "corrected_check_out", // 修正 退勤
    memo: "note",
    status: "logtime_status",
    creator: "creator_name",
    createdAt: "created_at",
  };

  const getSortApiField = (col: string): string | undefined => {
    if (SORT_FIELD_MAP[col]) return SORT_FIELD_MAP[col];
    return getV2FilterFieldFromColumn(col);
  };

  const mergedSorts: Record<string, "asc" | "desc"> = urlSearchParams
    ? { ...parseBracketSortsFromSearchParams(urlSearchParams) }
    : {};

  const allSortCols = uniqValues([
    ...FILTERABLE_COLUMN_KEYS,
    ...Object.keys(f),
    ...(urlSearchParams
      ? Object.keys(parseBracketSortsFromSearchParams(urlSearchParams)).map(
          (apiField) =>
            API_SORT_FIELD_TO_COLUMN[apiField] ||
            getColumnFromV2FilterField(apiField) ||
            apiField,
        )
      : []),
  ]);

  for (const col of allSortCols) {
    const apiField = getSortApiField(col);
    if (!apiField) continue;
    const dir = f[col]?.sort;
    if (dir === "asc" || dir === "desc") {
      mergedSorts[apiField] = dir;
    } else {
      delete mergedSorts[apiField];
    }
  }

  patch[MONTHLY_LIST_URL.sortBy] = null;
  patch[MONTHLY_LIST_URL.sortOrder] = null;
  if (urlSearchParams) {
    for (const key of Array.from(urlSearchParams.keys())) {
      if (key.startsWith("sort[") && key.endsWith("]")) {
        patch[key] = null;
      }
    }
  }
  for (const apiField of MONTHLY_LIST_SORT_API_FIELDS) {
    patch[monthlyListSortBracketKey(apiField)] = null;
  }
  for (const [apiField, dir] of Object.entries(mergedSorts)) {
    patch[monthlyListSortBracketKey(apiField)] = dir;
  }

  const applyDateColumnFilterPatch = (
    column: "createdAt" | "startAt" | "endAt",
    config: {
      filterSearchKey: string;
      filterAnyKey: string;
      gteqKey: string;
      lteqKey: string;
      legacyGteqKey?: string;
    },
  ) => {
    const checked = readChecked(column);
    const filterApi = uniqValues(
      checked
        .map((k) =>
          isNullFilterToken(k) ? k : toCreatedAtFilterOptionApiValue(k),
        )
        .filter(Boolean),
    );
    patch[config.filterSearchKey] = null;

    const colState = f[column];
    let isExplicitEmpty = false;
    if (colState && colState.filter && typeof colState.filter === "object") {
      const hasChecked = Object.values(colState.filter).some((v) => v === true);
      if (
        !hasChecked &&
        !colState.search?.trim() &&
        !colState.filter_special_empty
      ) {
        isExplicitEmpty = true;
      }
    }

    if (isExplicitEmpty) {
      patch[config.filterAnyKey] = "__empty_filter__";
    } else {
      patch[config.filterAnyKey] = filterApi.length
        ? filterApi.join(",")
        : null;
    }
    patch[config.gteqKey] = null;
    patch[config.lteqKey] = null;
    if (config.legacyGteqKey) patch[config.legacyGteqKey] = null;
  };

  applyDateColumnFilterPatch("startAt", {
    filterSearchKey: MONTHLY_LIST_URL.filterFirstCheckinTime,
    filterAnyKey: MONTHLY_LIST_URL.firstCheckinTimeAny,
    gteqKey: MONTHLY_LIST_URL.firstCheckinGteq,
    lteqKey: MONTHLY_LIST_URL.firstCheckinLteq,
    legacyGteqKey: MONTHLY_LIST_URL.legacyFirstCheckinGteq,
  });
  applyDateColumnFilterPatch("endAt", {
    filterSearchKey: MONTHLY_LIST_URL.filterLastCheckoutTime,
    filterAnyKey: MONTHLY_LIST_URL.lastCheckoutTimeAny,
    gteqKey: MONTHLY_LIST_URL.lastCheckoutGteq,
    lteqKey: MONTHLY_LIST_URL.lastCheckoutLteq,
  });
  applyDateColumnFilterPatch("createdAt", {
    filterSearchKey: MONTHLY_LIST_URL.filterCreatedAt,
    filterAnyKey: MONTHLY_LIST_URL.createdAtAny,
    gteqKey: MONTHLY_LIST_URL.createdAtGteq,
    lteqKey: MONTHLY_LIST_URL.createdAtLteq,
  });
  patch[MONTHLY_LIST_URL.dateIn] = null;

  if (urlSearchParams) {
    for (const key of Array.from(urlSearchParams.keys())) {
      if (
        (key.startsWith("filters[") || key.startsWith("search[")) &&
        key.endsWith("]")
      ) {
        patch[key] = null;
      }
    }
  }

  let hasUnmatchedSearch = false;

  for (const [columnId, columnState] of Object.entries(f)) {
    const apiField = getV2FilterFieldFromColumn(columnId);
    if (!apiField) continue;

    const selectedValues = Object.entries(columnState?.filter ?? {})
      .filter(([, checked]) => checked === true)
      .map(([value]) => value);
    const columnOptions = ((columnState?.filterOptions?.length
      ? columnState.filterOptions
      : fallbackFilterOptions?.[columnId]) || []) as Array<{
      value?: string | number;
      label?: string | number;
    }>;
    if (columnState?.filter_special_empty) {
      selectedValues.push("__NULL__");
    }

    const search = columnState?.search?.trim();
    patch[monthlyListSearchBracketKey(apiField)] = search || null;
    const searchMatchedValues = search
      ? matchFilterOptionValuesBySearch(search, columnOptions)
      : [];
    const filteredValues = search
      ? selectedValues.length > 0
        ? selectedValues.filter((value) => searchMatchedValues.includes(value))
        : searchMatchedValues
      : selectedValues;

    if (search && filteredValues.length === 0) {
      hasUnmatchedSearch = true;
    }

    const apiValues = uniqValues(
      filteredValues
        .map((value) =>
          columnId === "status"
            ? mapLogtimeStatusFilterToApi(value) ?? value
            : value,
        )
        .filter(Boolean),
    );

    patch[monthlyListFilterBracketKey(apiField)] = apiValues.length
      ? JSON.stringify(apiValues)
      : null;
  }

  if (hasUnmatchedSearch) {
    patch.empty_filter = "1";
  }

  return patch;
};

const isEmptyFilterState = (val: any) => {
  if (!val) return true;
  const checkVal = omit(val, ["sort", "filterOptions"]);
  if (Object.keys(checkVal).length === 0) return true;

  if ("filter" in checkVal && checkVal.filter !== undefined) {
    return false;
  }

  const hasSearch = !!checkVal.search;
  const hasSpecial = !!checkVal.filter_special_empty;

  return !hasSearch && !hasSpecial;
};

const cleanFilterState = (val: any) => {
  const cleaned = omit(val || {}, ["sort", "filterOptions"]);
  if (!cleaned.search) delete cleaned.search;
  if (!cleaned.filter_special_empty) delete cleaned.filter_special_empty;
  return cleaned;
};

const trimOrFirst = (raw: string | null): string | undefined => {
  const t = raw?.trim();
  return t ? t : undefined;
};

const formatJpDateTime = (iso: string | null | undefined): string => {
  if (iso == null || iso === "") return "";
  const m = moment.parseZone(iso);
  return m.isValid() ? m.format(DATE_TIME_FORMAT.DATE_TIME_JP) : String(iso);
};

const mapMonthlyToAgRows = (
  records: WorkerLogtimeMonthlyV2Record[],
  maxBreakPairs: number,
): TrackingAgRow[] =>
  records.map((record, index) => {
    const workerId = Number(record.worker_id);
    const name = record.worker_name || "";
    const checkinImages: string[] = [];
    const checkoutImages: string[] = [];
    const logtimeStatus = String(record?.logtime_status ?? "").trim();
    const row: TrackingAgRow = {
      id: `${workerId}-${record.date}`,
      workerId: Number.isFinite(workerId) ? workerId : 0,
      date: String(record.date),
      displayId: index + 1,
      employeeName: name,
      punchCheckIn: getWorkerLogtimeDisplayTime(record.punch?.check_in),
      punchCheckOut: getWorkerLogtimeDisplayTime(record.punch?.check_out),
      punchCheckInData: record.punch?.check_in ?? null,
      punchCheckOutData: record.punch?.check_out ?? null,
      correctedCheckIn: getWorkerLogtimeDisplayTime(record.corrected?.check_in),
      correctedCheckOut: getWorkerLogtimeDisplayTime(
        record.corrected?.check_out,
      ),
      correctedCheckInData: record.corrected?.check_in ?? null,
      correctedCheckOutData: record.corrected?.check_out ?? null,
      startAt: getWorkerLogtimeDisplayTime(record.punch?.check_in),
      endAt: getWorkerLogtimeDisplayTime(record.punch?.check_out),
      checkinImages,
      checkoutImages,
      checkinFilterLabel: checkinImages.length ? "あり" : "",
      checkoutFilterLabel: checkoutImages.length ? "あり" : "",
      memo: String((record.note as string | null | undefined) ?? ""),
      workingTime: record.working_time ?? "",
      breakTime: record.break_time ?? "",
      status: getTrackingStatusLabel(logtimeStatus),
      updaterName: record.updater_name ?? "",
      updatedAt: formatJpDateTime(record.updated_at ?? undefined),
      creator: record.updater_name ?? "",
      createdAt: formatJpDateTime(record.updated_at ?? undefined),
      canView: record.can_view !== false,
      canEdit: record.can_edit === true,
      canDelete: record.can_delete === true,
    };

    for (let breakIndex = 0; breakIndex < maxBreakPairs; breakIndex += 1) {
      row[`punchBreakStart${breakIndex}`] = getWorkerLogtimeDisplayTime(
        record.punch?.breaks?.[breakIndex]?.start,
      );
      row[`punchBreakEnd${breakIndex}`] = getWorkerLogtimeDisplayTime(
        record.punch?.breaks?.[breakIndex]?.end,
      );
      row[`punchBreakStart${breakIndex}Data`] =
        record.punch?.breaks?.[breakIndex]?.start ?? null;
      row[`punchBreakEnd${breakIndex}Data`] =
        record.punch?.breaks?.[breakIndex]?.end ?? null;
      row[`correctedBreakStart${breakIndex}`] = getWorkerLogtimeDisplayTime(
        record.corrected?.breaks?.[breakIndex]?.start,
      );
      row[`correctedBreakEnd${breakIndex}`] = getWorkerLogtimeDisplayTime(
        record.corrected?.breaks?.[breakIndex]?.end,
      );
      row[`correctedBreakStart${breakIndex}Data`] =
        record.corrected?.breaks?.[breakIndex]?.start ?? null;
      row[`correctedBreakEnd${breakIndex}Data`] =
        record.corrected?.breaks?.[breakIndex]?.end ?? null;
    }

    return row;
  });

const statusTag = (status: string, height?: any) => {
  const common = { bordered: true as const, style: { margin: 0 } };
  const chip = getTrackingStatusChipColors(status);
  return (
    <Tag
      {...common}
      style={{
        ...common.style,
        ...(chip
          ? {
              borderColor: chip.borderColor,
              color: chip.color,
              background: chip.background,
            }
          : {}),
        height: height - 5,
        display: "flex",
        alignItems: "center",
      }}
    >
      {status}
    </Tag>
  );
};

const useTableList = (props: ReceivedProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentWorkerId } = useAttendanceManagementPermission();
  const gridRef: MutableRefObject<any> = useRef(null);
  const [gridApi, setGridApi] = useState<any>(null);
  const [columnApi, setColumnApi] = useState<any>(null);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const lastPushedSigRef = useRef<string | null>(null);

  const { detailBase } = useManagerTrackingPath();
  const displaySize =
    (useSelector(getDisplaySize) as
      | keyof typeof FONT_SIZE
      | keyof typeof BUTTON_HEIGHT) || DISPLAY_SIZES.SMALL;
  const fontSize = FONT_SIZE[displaySize];
  const height = BUTTON_HEIGHT[displaySize];
  const rowHeight =
    ROW_HEIGHT_E_LEARNING[displaySize as keyof typeof ROW_HEIGHT_E_LEARNING];
  const tableHeight = useTrackingViewportHeight(
    TRACKING_LIST_TABLE_HEIGHT_OFFSET,
  );

  const minWidth = {
    small: 130,
    medium: 150,
    large: 170,
  }[displaySize];

  const {
    monthlyRecords = [],
    monthlyMaxBreakPairs = 0,
    monthlyMeta,
    loading = false,
    currentPage = 1,
    pageSize = 50,
    onChangePagination,
    preHeaderChildren,
    companyId,
    onAttendanceSaved,
    tableFormRef,
    isEditMode = false,
    handleSetStateForm,
  } = props;

  const rowData: TrackingAgRow[] = useMemo(
    () => mapMonthlyToAgRows(monthlyRecords, monthlyMaxBreakPairs),
    [monthlyMaxBreakPairs, monthlyRecords],
  );
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingDetail, setEditingDetail] =
    useState<LogTimeByDateResponse | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorError, setEditorError] = useState("");
  const [editorSavedMessage, setEditorSavedMessage] = useState("");
  const clearInlineEditorMessages = useCallback(() => {
    setEditorError("");
    setEditorSavedMessage("");
  }, []);
  const closeInlineEditor = useCallback(() => {
    setEditingRowId(null);
    setEditingDetail(null);
    setEditorError("");
    setEditorSavedMessage("");
  }, []);

  const loadInlineEditor = useCallback(
    async (row: TrackingAgRow) => {
      if (editingRowId === row.id) {
        closeInlineEditor();
        return;
      }
      if (companyId == null || String(companyId).trim() === "") {
        setEditorError("会社情報を確認できないため、勤怠を編集できません。");
        return;
      }
      setEditingRowId(row.id);
      setEditingDetail(null);
      setEditorLoading(true);
      setEditorError("");
      setEditorSavedMessage("");
      try {
        const detail = await fetchWorkerLogTimesByDate({
          companyId: String(companyId),
          date: row.date,
          workerId: row.workerId,
        });
        setEditingDetail(detail);
      } catch (error) {
        setEditorError(
          getWorkerLogtimeApiErrorMessage(
            error,
            "勤怠データを読み込めませんでした。",
          ),
        );
      } finally {
        setEditorLoading(false);
      }
    },
    [closeInlineEditor, companyId, editingRowId],
  );

  const saveInlineEditor = useCallback(
    async (items: WorkerLogtimesSaveJsonItem[]) => {
      if (!editingDetail || companyId == null) return;
      setEditorSaving(true);
      setEditorError("");
      setEditorSavedMessage("");
      try {
        await saveWorkerLogtimes({
          worker_id: editingDetail.worker_id,
          company_id: String(companyId),
          date: editingDetail.date,
          logtimes: items,
        });
        await onAttendanceSaved?.();
        const refreshed = await fetchWorkerLogTimesByDate({
          companyId: String(companyId),
          date: editingDetail.date,
          workerId: editingDetail.worker_id,
        });
        setEditingDetail(refreshed);
        setEditorSavedMessage(
          "変更を保存しました。元の打刻と修正内容は履歴に保持されています。",
        );
      } catch (error) {
        setEditorError(
          getWorkerLogtimeApiErrorMessage(
            error,
            "勤怠の変更を保存できませんでした。",
          ),
        );
      } finally {
        setEditorSaving(false);
      }
    },
    [companyId, editingDetail, onAttendanceSaved],
  );

  const expandedRowHeight = useMemo(() => {
    const logtimeCount =
      editingDetail?.logtimes?.filter((logtime) => logtime.log_type !== "note")
        .length ?? 2;
    const historyCount = Math.min(
      editingDetail?.change_histories?.length ?? 0,
      4,
    );
    return Math.max(560, 225 + logtimeCount * 40 + historyCount * 22);
  }, [editingDetail]);

  useEffect(() => {
    gridApi?.resetRowHeights?.();
  }, [editingDetail, editingRowId, expandedRowHeight, gridApi]);
  const year = searchParams.get("year") ?? moment().format("YYYY");
  const month = searchParams.get("month") ?? moment().format("MM");
  const pushListQuery = useCallback(
    (patch: MonthlyListSearchPatch, resetPage = true) => {
      const sp = searchParamsRef.current;
      const merged = applyMonthlyListPatch(sp, patch);
      if (resetPage) merged.set("page", "1");
      lastPushedSigRef.current = monthlyListFiltersSignature(merged);
      setSearchParams(merged);
    },
    [setSearchParams],
  );

  const [filterInfo, setFilterInfo] = useState<IFilterInfo>({
    open: false,
    columnId: null,
    position: { x: 0, y: 0 },
  });
  const initialFilter = useMemo(
    () => buildTrackingFilterStateFromUrl(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [filter, setFilter] = useState<TrackingFilterState>(initialFilter);
  const [filterSubmitSeq, setFilterSubmitSeq] = useState(0);
  const filterOrderRef = useRef<FilterableColumnKey[]>(
    Object.keys(initialFilter) as FilterableColumnKey[],
  );
  const filterOptionsCacheRef = useRef<
    Record<number, Record<FilterableColumnKey, IOptions>>
  >({});
  const currentFilterSignature = useMemo(
    () => monthlyListFiltersSignature(searchParams),
    [searchParams],
  );

  const prevYearMonthRef = useRef(`${year}-${month}`);
  useEffect(() => {
    const currentYM = `${year}-${month}`;
    if (prevYearMonthRef.current !== currentYM) {
      filterOptionsCacheRef.current = {};
      prevYearMonthRef.current = currentYM;
    }
  }, [year, month]);

  useEffect(() => {
    if (lastPushedSigRef.current === currentFilterSignature) {
      lastPushedSigRef.current = null;
      return;
    }

    lastPushedSigRef.current = null;
    const newFilter = buildTrackingFilterStateFromUrl(searchParamsRef.current);
    setFilter(newFilter);
    const activeKeys = Object.keys(newFilter);
    filterOrderRef.current = filterOrderRef.current.filter((col) =>
      activeKeys.includes(col),
    );
  }, [currentFilterSignature]);

  const filterOptionsParams = useMemo(() => {
    const noteFromSearch = searchParams.get(MONTHLY_LIST_URL.noteCont)?.trim();
    const noteFromUrlAny = splitCsvParam(
      searchParams.get(MONTHLY_LIST_URL.noteContAny),
    );
    const noteFromPanel = Object.keys(filter.memo?.filter ?? {}).filter(
      Boolean,
    );
    const noteFilters = uniqValues([
      ...(noteFromSearch ? [noteFromSearch] : []),
      ...noteFromUrlAny,
      ...noteFromPanel,
    ]);

    const statusFromUrlAny = splitCsvParam(
      searchParams.get(MONTHLY_LIST_URL.logtimeStatusAny),
    );
    const statusFromPanel = Object.keys(filter.status?.filter ?? {})
      .filter(Boolean)
      .map((s) => mapLogtimeStatusFilterToApi(s) ?? s)
      .flatMap((s) => splitCsvParam(s));
    const statusFilters = uniqValues([...statusFromUrlAny, ...statusFromPanel]);

    const workerNameFromSearch =
      searchParams.get(MONTHLY_LIST_URL.workerNameCont)?.trim() ||
      filter.employeeName?.search?.trim();
    const workerNameFilters = uniqValues([
      ...(workerNameFromSearch ? [workerNameFromSearch] : []),
      ...splitCsvParam(searchParams.get(MONTHLY_LIST_URL.workerNameContAny)),
      ...Object.keys(filter.employeeName?.filter ?? {}).filter(Boolean),
    ]);
    const creatorNameFromSearch =
      searchParams.get(MONTHLY_LIST_URL.creatorNameCont)?.trim() ||
      filter.creator?.search?.trim();
    const creatorNameFilters = uniqValues([
      ...(creatorNameFromSearch ? [creatorNameFromSearch] : []),
      ...splitCsvParam(searchParams.get(MONTHLY_LIST_URL.creatorNameContAny)),
      ...Object.keys(filter.creator?.filter ?? {}).filter(Boolean),
    ]);
    const createdAtFromSearch =
      searchParams.get(MONTHLY_LIST_URL.filterCreatedAt)?.trim() ||
      filter.createdAt?.search?.trim();
    const dateFilters = uniqValues(
      [
        ...(createdAtFromSearch ? [createdAtFromSearch] : []),
        ...splitCsvParam(searchParams.get(MONTHLY_LIST_URL.createdAtAny)),
        ...splitCsvParam(searchParams.get(MONTHLY_LIST_URL.dateIn)),
        ...Object.keys(filter.createdAt?.filter ?? {}).filter(Boolean),
      ].map(normalizeCreatedAtActiveFilterValue),
    );
    const firstCheckinFromSearch =
      searchParams.get(MONTHLY_LIST_URL.filterFirstCheckinTime)?.trim() ||
      filter.startAt?.search?.trim();
    const firstCheckinDateFilters = uniqValues(
      [
        ...(firstCheckinFromSearch ? [firstCheckinFromSearch] : []),
        ...splitCsvParam(
          searchParams.get(MONTHLY_LIST_URL.firstCheckinTimeAny),
        ),
        ...Object.keys(filter.startAt?.filter ?? {}).filter(Boolean),
      ].map(normalizeCreatedAtActiveFilterValue),
    );
    const lastCheckoutFromSearch =
      searchParams.get(MONTHLY_LIST_URL.filterLastCheckoutTime)?.trim() ||
      filter.endAt?.search?.trim();
    const lastCheckoutDateFilters = uniqValues(
      [
        ...(lastCheckoutFromSearch ? [lastCheckoutFromSearch] : []),
        ...splitCsvParam(
          searchParams.get(MONTHLY_LIST_URL.lastCheckoutTimeAny),
        ),
        ...Object.keys(filter.endAt?.filter ?? {}).filter(Boolean),
      ].map(normalizeCreatedAtActiveFilterValue),
    );
    const activeFilters = {
      ...(workerNameFilters.length ? { worker_names: workerNameFilters } : {}),
      ...(creatorNameFilters.length
        ? { updater_names: creatorNameFilters }
        : {}),
      ...(dateFilters.length ? { updated_at: dateFilters } : {}),
      ...(firstCheckinDateFilters.length
        ? { punch_check_in: firstCheckinDateFilters }
        : {}),
      ...(lastCheckoutDateFilters.length
        ? { punch_check_out: lastCheckoutDateFilters }
        : {}),
      ...(noteFilters.length ? { notes: noteFilters } : {}),
      ...(statusFilters.length ? { logtime_statuses: statusFilters } : {}),
      ...parseBracketFiltersFromSearchParams(searchParams),
    };
    if (!companyId) return null;

    return {
      company_id: companyId,
      year: Number(year),
      month: Number(month),
      ...(Object.keys(activeFilters).length
        ? { active_filters: activeFilters }
        : {}),
    };
  }, [
    year,
    month,
    filter.memo,
    filter.status,
    filter.createdAt,
    filter.employeeName,
    filter.endAt,
    filter.startAt,
    filter.creator,
    searchParams,
    currentFilterSignature,
    filterSubmitSeq,
    companyId,
  ]);
  const { data: filterOptionsApi, isFetching: _isFetchingFilterOptions } =
    useFetchWorkerLogtimesFilterOptionsV2(filterOptionsParams);
  const isFetchingFilterOptions =
    filterOptionsParams === null ? false : _isFetchingFilterOptions;

  const filterData = useMemo(() => {
    const out: Record<string, string[]> = {};
    FILTERABLE_COLUMN_KEYS.forEach((key) => {
      out[key] = rowData.map((r) => String(r[key] ?? ""));
    });

    for (
      let breakIndex = 0;
      breakIndex < monthlyMaxBreakPairs;
      breakIndex += 1
    ) {
      [
        `punchBreakStart${breakIndex}`,
        `punchBreakEnd${breakIndex}`,
        `correctedBreakStart${breakIndex}`,
        `correctedBreakEnd${breakIndex}`,
      ].forEach((key) => {
        out[key] = rowData.map((row) => String(row[key] ?? ""));
      });
    }

    return out;
  }, [monthlyMaxBreakPairs, rowData]);

  const apiFilterOptionsByColumn = useMemo<
    Record<FilterableColumnKey, IOptions>
  >(() => {
    const calendarDayLabelsFromIsoValues = (values: string[]): string[] => {
      const raw = values.map((v) => String(v ?? ""));
      return uniqValues(
        raw
          .filter((v) => !isNullFilterToken(v))
          .map((iso) => jpDateTimeStringToJpCalendarDay(iso))
          .filter((v): v is string => Boolean(v)),
      ).sort();
    };
    const dateColumnFilterOptions = (
      apiValues: string[] | undefined,
      rowCells: string[],
    ): IOptions => {
      const source =
        apiValues && apiValues.length > 0
          ? apiValues.map(String)
          : rowCells.map(String);
      const nullTokens = extractNullFilterTokens(source);
      const days = calendarDayLabelsFromIsoValues(source);
      return toFilterOptions([...nullTokens, ...days]);
    };
    const getRawOptions = (...fields: string[]) => {
      for (const field of fields) {
        const options = filterOptionsApi?.[field];
        if (options?.length) return options;
      }
      return [];
    };
    const getOptionValues = (...fields: string[]) =>
      getRawOptions(...fields).map((option) =>
        typeof option === "string" ? option : String(option.value ?? ""),
      );
    const createdAtApiValues = getOptionValues("updated_at", "created_at");
    const createdAtNullTokens = extractNullFilterTokens(createdAtApiValues);
    const createdAtRowDays = uniqValues(
      (filterData.createdAt || [])
        .map((cell) => jpDateTimeStringToJpCalendarDay(String(cell)))
        .filter((v): v is string => Boolean(v)),
    ).sort();

    const optionsByColumn: Record<FilterableColumnKey, IOptions> = {
      employeeName: toFilterOptions(
        normalizeTextFilterValues(getOptionValues("worker_names")),
      ),
      date: toFilterOptions(
        normalizeTextFilterValues(getOptionValues("dates")),
      ),
      endAt: toFilterOptions(
        normalizeTextFilterValues(
          getOptionValues("punch_check_out", "last_checkout_time").length
            ? getOptionValues("punch_check_out", "last_checkout_time")
            : filterData.endAt || [],
        ),
      ),
      startAt: toFilterOptions(
        normalizeTextFilterValues(
          getOptionValues("punch_check_in", "first_checkin_time").length
            ? getOptionValues("punch_check_in", "first_checkin_time")
            : filterData.startAt || [],
        ),
      ),
      correctedCheckIn: toFilterOptions(
        normalizeTextFilterValues(getOptionValues("corrected_check_in")),
      ),
      correctedCheckOut: toFilterOptions(
        normalizeTextFilterValues(getOptionValues("corrected_check_out")),
      ),
      memo: toFilterOptions(
        normalizeTextFilterValues(getOptionValues("notes", "note")),
      ),
      workingTime: toFilterOptions(
        normalizeTextFilterValues(getOptionValues("working_hours")),
      ),
      breakTime: toFilterOptions(
        normalizeTextFilterValues(getOptionValues("break_hours")),
      ),
      status: getRawOptions("logtime_statuses").map((option) => {
        const label =
          typeof option === "string"
            ? getTrackingStatusLabel(option)
            : option.label;
        return { value: label, label };
      }),
      creator: toFilterOptions(
        normalizeTextFilterValues(
          getOptionValues("updater_names", "creator_names"),
        ),
      ),
      createdAt: createdAtApiValues.length
        ? [
            ...toFilterOptions(createdAtNullTokens),
            ...createdAtApiValues
              .filter((value) => !isNullFilterToken(value))
              .map((value) => ({
                value,
                label: formatApiDateTimeForFilterDisplay(value) || value,
              })),
          ]
        : toFilterOptions(createdAtRowDays),
    };

    for (
      let breakIndex = 0;
      breakIndex < monthlyMaxBreakPairs;
      breakIndex += 1
    ) {
      const apiBreakIndex = breakIndex + 1;
      optionsByColumn[`punchBreakStart${breakIndex}`] = toFilterOptions(
        normalizeTextFilterValues(
          getOptionValues(`punch_break_${apiBreakIndex}_start`),
        ),
      );
      optionsByColumn[`punchBreakEnd${breakIndex}`] = toFilterOptions(
        normalizeTextFilterValues(
          getOptionValues(`punch_break_${apiBreakIndex}_end`),
        ),
      );
      optionsByColumn[`correctedBreakStart${breakIndex}`] = toFilterOptions(
        normalizeTextFilterValues(
          getOptionValues(`corrected_break_${apiBreakIndex}_start`),
        ),
      );
      optionsByColumn[`correctedBreakEnd${breakIndex}`] = toFilterOptions(
        normalizeTextFilterValues(
          getOptionValues(`corrected_break_${apiBreakIndex}_end`),
        ),
      );
    }

    return optionsByColumn;
  }, [filterOptionsApi, filterData, monthlyMaxBreakPairs]);

  useEffect(() => {
    if (!isFetchingFilterOptions && filterOptionsApi) {
      const depth = filterOrderRef.current.length;
      filterOptionsCacheRef.current[depth] = apiFilterOptionsByColumn;
    }
  }, [apiFilterOptionsByColumn, isFetchingFilterOptions, filterOptionsApi]);

  const handleSetFilter = useCallback(
    (nextPartial: TrackingFilterState) => {
      setFilterSubmitSeq((prev) => prev + 1);
      setFilter((prev) => {
        const next: TrackingFilterState = { ...prev, ...nextPartial };
        const partialCols = Object.keys(nextPartial) as FilterableColumnKey[];

        // Track if any column is being sorted
        let sortedColumn: FilterableColumnKey | null = null;
        let changedKey: FilterableColumnKey | null = null;

        for (const col of partialCols) {
          if (!col) continue;

          // Check if filter part actually changed (ignore sort changes)
          const prevClean = cleanFilterState(prev[col]);
          const nextClean = cleanFilterState(nextPartial[col]);

          if (!isEqual(prevClean, nextClean)) {
            changedKey = col;
          }

          const savedSort = nextPartial[col]?.sort;

          if (savedSort === "asc" || savedSort === "desc") {
            next[col] = { ...(next[col] || {}), sort: savedSort };
            sortedColumn = col;
            continue;
          }

          if (savedSort === "") {
            if (next[col]) {
              const { sort: _ignoredSort, ...rest } = next[col];
              next[col] = rest;
            }
            continue;
          }

          if (next[col]) {
            const prevSort = prev[col]?.sort;
            if (prevSort === "asc" || prevSort === "desc") {
              next[col] = { ...next[col], sort: prevSort };
            } else {
              const { sort: _ignoredSort, ...rest } = next[col];
              next[col] = rest;
            }
          }
        }

        // If a new sort is being applied, clear sort from all other columns
        // Check if any column in next has a new sort value (comparing with prev)
        const newSortColumn = Object.keys(next).find((col) => {
          return (
            "sort" in next[col] &&
            (!("sort" in (prev[col] || {})) ||
              next[col].sort !== (prev[col] as any)?.sort)
          );
        });

        if (newSortColumn) {
          const columnsToDelete: FilterableColumnKey[] = [];

          Object.keys(next).forEach((col) => {
            if (col !== newSortColumn && "sort" in next[col]) {
              const { sort: _ignoredSort, ...rest } = next[col];
              if (Object.keys(rest).length > 0) {
                next[col as FilterableColumnKey] = rest;
              } else {
                // Mark columns with only sort for deletion
                columnsToDelete.push(col as FilterableColumnKey);
              }
            }
          });

          // Explicitly delete columns that had only sort
          columnsToDelete.forEach((col) => {
            delete next[col];
          });
        }
        if (changedKey) {
          let currentOrder = filterOrderRef.current;
          const changedIndex = currentOrder.indexOf(changedKey);

          if (changedIndex === -1) {
            if (!isEmptyFilterState(next[changedKey])) {
              currentOrder.push(changedKey);
            }
          } else {
            // Trim to this changed index
            currentOrder = currentOrder.slice(0, changedIndex + 1);
            if (isEmptyFilterState(next[changedKey])) {
              currentOrder.pop(); // Remove if it's now empty
            }
          }
          filterOrderRef.current = currentOrder;

          // Clear downstream filters from `next`
          const nextKeys = Object.keys(next) as FilterableColumnKey[];
          for (const key of nextKeys) {
            if (!currentOrder.includes(key)) {
              if (next[key]) {
                const { sort } = next[key]!;
                // Clear filter, but keep sort if it exists
                if (sort) {
                  next[key] = { sort };
                } else {
                  delete next[key];
                }
              }
            }
          }
        }

        const patch = buildPatchFromPanelFilter(
          next,
          apiFilterOptionsByColumn,
          searchParamsRef.current,
        );
        pushListQuery(patch, true);
        return next;
      });
    },
    [pushListQuery, apiFilterOptionsByColumn],
  );

  const selectedFilterOptions = useMemo(() => {
    const col = String(filterInfo.columnId || "") as FilterableColumnKey;
    if (!col) return [] as IOptions;

    let targetDepth = filterOrderRef.current.indexOf(col);
    if (targetDepth === -1) {
      targetDepth = filterOrderRef.current.length;
    }

    let hasUpstreamEmpty = false;

    for (let i = 0; i < targetDepth; i++) {
      const upstreamCol = filterOrderRef.current[i];
      const upstreamState = filter[upstreamCol];

      if (
        upstreamState &&
        upstreamState.filter &&
        typeof upstreamState.filter === "object"
      ) {
        const hasChecked = Object.values(upstreamState.filter).some(
          (v) => v === true,
        );
        if (
          !hasChecked &&
          !upstreamState.search?.trim() &&
          !upstreamState.filter_special_empty
        ) {
          hasUpstreamEmpty = true;
          break;
        }
      }
    }

    if (hasUpstreamEmpty) {
      return [] as IOptions;
    }

    const cachedOptions = filterOptionsCacheRef.current[targetDepth]?.[col];

    return (cachedOptions ||
      apiFilterOptionsByColumn[col] ||
      toFilterOptions(filterData[col] || [])) as IOptions;
  }, [filterInfo.columnId, apiFilterOptionsByColumn, filterData, filter]);

  const selectedFilterType = useMemo(() => {
    const col = String(filterInfo.columnId || "") as FilterableColumnKey;
    if (col === "createdAt" || col === "date") return "date";
    if (
      [
        "startAt",
        "endAt",
        "correctedCheckIn",
        "correctedCheckOut",
        "workingTime",
        "breakTime",
      ].includes(col) ||
      /^(punch|corrected)Break(Start|End)\d+$/.test(col)
    ) {
      return "time";
    }
    return undefined;
  }, [filterInfo.columnId]);

  const onGridReady = useCallback((params: any) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi ?? params.api);
  }, []);

  useEffect(() => {
    if (!gridApi) return;
    gridApi.resetRowHeights();
    gridApi.refreshCells({ force: true });
  }, [gridApi, rowHeight]);

  const actionsColWidth = useMemo(() => {
    const BASE_WIDTH = 180;
    const EXTRA_WIDTH_PER_DIGIT = 8;
    const MAX_INDEX_DIGITS_AT_BASE_WIDTH = 2;
    if (!rowData.length) return BASE_WIDTH;
    const maxRowIndex = rowData.reduce(
      (max, row) => Math.max(max, row.displayId + (currentPage - 1) * pageSize),
      0,
    );
    const digits = String(maxRowIndex).length;
    const extraDigits = Math.max(0, digits - MAX_INDEX_DIGITS_AT_BASE_WIDTH);
    return BASE_WIDTH + extraDigits * EXTRA_WIDTH_PER_DIGIT;
  }, [rowData, currentPage, pageSize]);

  useEffect(() => {
    if (!columnApi) return;
    columnApi.setColumnWidth("actions", actionsColWidth);
  }, [columnApi, actionsColWidth]);

  const FilterHeader = useCallback(
    (params: { displayName: string; columnId?: FilterableColumnKey }) => {
      const colId = params.columnId || "";
      const isActive = (() => {
        const current = filter[colId];
        if (!current) return false;
        const hasSearch = Boolean(current.search?.trim());
        const hasFilterValues = Boolean(
          current.filter && Object.keys(current.filter).length > 0,
        );
        return hasSearch || hasFilterValues;
      })();
      return (
        <div className="title_container">
          <TextToolTip style={{ padding: 0 }}>{params.displayName}</TextToolTip>
          <FilterIcon
            showFilterIcon
            setFilterInfo={setFilterInfo}
            columnId={colId}
            filter={filter}
            isActive={isActive}
            skipParseColumnId
          />
        </div>
      );
    },
    [filter],
  );

  const columnDefs: any[] = useMemo(() => {
    const isMissingTimeValue = (
      rowData: TrackingAgRow | undefined,
      field: string,
    ) => {
      if (!rowData) return false;
      if (getOriginalCellData(rowData, field)?.missing) return true;

      const checkOutCheckInField: Record<string, string> = {
        correctedCheckOut: "correctedCheckIn",
      };
      const checkInField = checkOutCheckInField[field];
      const breakEndField = field.match(/^(corrected)BreakEnd(\d+)$/);
      const breakStartField = breakEndField
        ? `${breakEndField[1]}BreakStart${breakEndField[2]}`
        : undefined;

      return (
        (!!checkInField && !!rowData[checkInField] && !rowData[field]) ||
        (!!breakStartField && !!rowData[breakStartField] && !rowData[field])
      );
    };

    const getTimeColumnMinWidth = (
      headerName: string,
      isEditableColumn = false,
    ) => {
      const currentDisplaySize = displaySize;
      const minimumWidth = {
        [DISPLAY_SIZES.SMALL]: 50,
        [DISPLAY_SIZES.MEDIUM]: 50,
        [DISPLAY_SIZES.LARGE]: 65,
      }[currentDisplaySize];
      const headerTextWidth =
        Array.from(headerName).length *
        Number.parseInt(FONT_SIZE[currentDisplaySize], 10);
      const filterIconAndSpacingWidth = BUTTON_HEIGHT[currentDisplaySize] + 8;
      if (isEditMode && isEditableColumn) {
        return Math.max(
          minimumWidth,
          headerTextWidth + filterIconAndSpacingWidth,
          70,
        );
      }
      return Math.max(
        minimumWidth,
        headerTextWidth + filterIconAndSpacingWidth,
      );
    };

    const timeColumn = (
      headerName: string,
      field: string,
      isEditableColumn = false,
    ) => ({
      headerName,
      colId: field,
      field,
      minWidth: getTimeColumnMinWidth(headerName, isEditableColumn),
      sortable: false,
      filter: false,
      floatingFilter: false,
      headerComponentFramework: FilterHeader,
      headerComponentParams: { columnId: field },
      cellStyle: {
        textAlign: "center",
      },
      cellRenderer: (params: TimeCellRendererParams) => {
        const rowData = params.data;

        if (isMissingTimeValue(rowData, field)) {
          return <div className="missed">未入力</div>;
        }

        const originalCellData = getOriginalCellData(params.data, field);
        const editCount = originalCellData?.edit_count || 0;
        const showTooltipEditCount = editCount > 0;
        return (
          <CellHistory
            showTooltipEditCount={showTooltipEditCount}
            editCount={editCount}
            params={params}
            companyId={companyId}
            workerId={params.data?.workerId ?? 0}
            date={params.data?.date ?? ""}
            workerLogtimeId={originalCellData?.id}
          />
        );
      },
    });
    const correctedTimeColumn = (headerName: string, field: string) => ({
      ...timeColumn(headerName, field, true),
      editInfo: {
        type: TYPE_INPUT.EXTENDED_TIME,
        getDisabledData: (rowData: TrackingAgRow) =>
          (!getOriginalCellData(rowData, field)?.id &&
            !isMissingTimeValue(rowData, field)) ||
          !(rowData.canEdit || rowData.canDelete),
        getDisabledTimeData: (rowData: TrackingAgRow) =>
          getTrackingDisabledTimeData(rowData, field, correctedTimeFields),
        rules: [],
        extendedTimeHourCount:
          field === "correctedCheckIn"
            ? CHECK_IN_HOUR_COUNT
            : EXTENDED_TIME_HOUR_COUNT,
      },
    });
    const breakIndexes = Array.from(
      { length: monthlyMaxBreakPairs },
      (_, index) => index,
    );
    const correctedTimeFields = [
      "correctedCheckIn",
      ...breakIndexes.flatMap((breakIndex) => [
        `correctedBreakStart${breakIndex}`,
        `correctedBreakEnd${breakIndex}`,
      ]),
      "correctedCheckOut",
    ];

    return [
      {
        headerName: "",
        colId: "actions",
        field: "displayId",
        pinned: "left" as const,
        lockPosition: true,
        minWidth: actionsColWidth,
        sortable: false,
        filter: false,
        floatingFilter: false,
        suppressMovable: true,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        cellRenderer: (params: { data: TrackingAgRow }) => {
          const row = params.data;
          if (!row) return null;
          const detailUrl = `${detailBase}?worker_id=${row.workerId}&date=${row.date}`;
          const rowIndex = row.displayId + (currentPage - 1) * pageSize;
          const maxRowIndex = rowData.length + 1 + (currentPage - 1) * pageSize;

          return (
            <Space size={6} className="tracking-list-actions">
              <span
                className="mr-2"
                style={{
                  display: "inline-flex",
                  justifyContent: "center",
                  width: `${maxRowIndex.toString().length}ch`,
                  flexShrink: 0,
                  marginLeft: 4,
                }}
              >
                {rowIndex}
              </span>
              <ButtonCommon
                disabled={
                  (row.workerId !== currentWorkerId &&
                    !(row.canEdit || row.canDelete)) ||
                  editorSaving ||
                  isEditMode
                }
                onClick={() => void loadInlineEditor(row)}
                style={{
                  fontSize,
                  width: "fit-content",
                  height: height - 2,
                }}
                name={editingRowId === row.id ? "閉じる" : "編集"}
              />
              <ButtonCommon
                disabled={!row.canView || isEditMode}
                onClick={() =>
                  navigate(detailUrl, {
                    state: { canView: row.canView, canEdit: row.canEdit },
                  })
                }
                style={{
                  fontSize,
                  width: "fit-content",
                  height: height - 2,
                }}
                name="詳細"
              />
            </Space>
          );
        },
      },
      {
        headerName: "",
        children: [
          {
            headerName: "スタッフ名",
            colId: "employeeName",
            field: "employeeName",
            minWidth: 150,
            sortable: false,
            filter: false,
            floatingFilter: false,
            headerComponentFramework: FilterHeader,
            headerComponentParams: { columnId: "employeeName" },
            cellRenderer: (params: { value?: string | null }) => (
              <TextToolTip style={{ padding: 0 }}>
                {params.value || ""}
              </TextToolTip>
            ),
          },
          {
            headerName: "日付",
            colId: "date",
            field: "date",
            minWidth: 120,
            sortable: false,
            filter: false,
            floatingFilter: false,
            headerComponentFramework: FilterHeader,
            headerComponentParams: { columnId: "date" },
            colSpan: (params: { data?: TrackingAgRow }) =>
              params.data?.id === editingRowId
                ? 10 + monthlyMaxBreakPairs * 4
                : 1,
            cellStyle: (params: { data?: TrackingAgRow }) =>
              params.data?.id === editingRowId
                ? {
                    display: "block",
                    padding: "6px",
                    overflow: "visible",
                    zIndex: 2,
                  }
                : { display: "flex", alignItems: "center", padding: 0 },
            cellRenderer: (params: {
              value?: string | null;
              data?: TrackingAgRow;
            }) => {
              if (params.data?.id !== editingRowId) {
                return (
                  <TextToolTip style={{ padding: 0, textAlign: "center" }}>
                    {params.value ?? ""}
                  </TextToolTip>
                );
              }
              if (editorLoading) {
                return (
                  <div data-testid="attendance-editor-loading">
                    読み込み中...
                  </div>
                );
              }
              if (!editingDetail) {
                return (
                  <Alert
                    type="error"
                    showIcon
                    message={
                      editorError || "勤怠データを読み込めませんでした。"
                    }
                  />
                );
              }
              return (
                <InlineAttendanceEditor
                  data={editingDetail}
                  saving={editorSaving}
                  serverError={editorError}
                  savedMessage={editorSavedMessage}
                  onSave={saveInlineEditor}
                  onCancel={closeInlineEditor}
                  onClearMessages={clearInlineEditorMessages}
                />
              );
            },
          },
        ],
      },
      {
        headerName: "打刻時間",
        children: [
          {
            ...timeColumn("出勤", "punchCheckIn"),
            colId: "startAt",
            headerComponentFramework: FilterHeader,
            headerComponentParams: { columnId: "startAt" },
          },
          {
            ...timeColumn("退勤", "punchCheckOut"),
            colId: "endAt",
            headerComponentFramework: FilterHeader,
            headerComponentParams: { columnId: "endAt" },
          },
          ...breakIndexes.flatMap((breakIndex) => [
            timeColumn("休憩開始", `punchBreakStart${breakIndex}`),
            timeColumn("休憩終了", `punchBreakEnd${breakIndex}`),
          ]),
        ],
      },
      {
        headerName: "修正時間",
        children: [
          correctedTimeColumn("出勤", "correctedCheckIn"),
          correctedTimeColumn("退勤", "correctedCheckOut"),
          ...breakIndexes.flatMap((breakIndex) => [
            correctedTimeColumn("休憩開始", `correctedBreakStart${breakIndex}`),
            correctedTimeColumn("休憩終了", `correctedBreakEnd${breakIndex}`),
          ]),
        ],
      },
      {
        headerName: "",
        children: [
          {
            headerName: "MEMO",
            colId: "memo",
            field: "memo",
            minWidth: 160,
            sortable: false,
            filter: false,
            floatingFilter: false,
            headerComponentFramework: FilterHeader,
            headerComponentParams: { columnId: "memo" },
            editInfo: {
              type: TYPE_INPUT.TEXT_AREA,
              options: [],
              rules: [],
              getDisabledData: (rowData: TrackingAgRow) =>
                rowData.workerId !== currentWorkerId &&
                !(rowData.canEdit || rowData.canDelete),
            },
            cellRenderer: (params: { value?: string | null }) => (
              <TextToolTip
                isTextMultipleLine={
                  (params?.value?.split("\n")?.length || 0) > 1
                }
                style={{ padding: 0 }}
              >
                {params.value || ""}
              </TextToolTip>
            ),
          },
          {
            ...timeColumn("労働時間", "workingTime"),
            minWidth: minWidth,
            cellRenderer: (params: { value?: string | null }) => (
              <TextToolTip
                isTextMultipleLine={
                  (params?.value?.split("\n")?.length || 0) > 1
                }
                style={{ padding: 0, textAlign: "center" }}
              >
                {params.value || ""}
              </TextToolTip>
            ),
          },
          {
            ...timeColumn("休憩時間", "breakTime"),
            minWidth: minWidth,
            cellRenderer: (params: { value?: string | null }) => (
              <TextToolTip
                isTextMultipleLine={
                  (params?.value?.split("\n")?.length || 0) > 1
                }
                style={{ padding: 0 }}
              >
                {params.value || ""}
              </TextToolTip>
            ),
          },
          {
            headerName: "ステータス",
            colId: "status",
            field: "status",
            minWidth: 140,
            sortable: false,
            filter: false,
            floatingFilter: false,
            headerComponentFramework: FilterHeader,
            headerComponentParams: { columnId: "status" },
            cellStyle: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            cellRenderer: (params: { value?: string | null }) =>
              statusTag(params.value ?? "", height),
          },
          {
            headerName: "更新者",
            colId: "creator",
            field: "updaterName",
            minWidth: 130,
            sortable: false,
            filter: false,
            floatingFilter: false,
            headerComponentFramework: FilterHeader,
            headerComponentParams: { columnId: "creator" },
            cellRenderer: (params: { value?: string | null }) => (
              <TextToolTip style={{ padding: 0 }}>
                {params.value || ""}
              </TextToolTip>
            ),
          },
          {
            headerName: "更新日時",
            colId: "createdAt",
            field: "updatedAt",
            minWidth: 180,
            sortable: false,
            filter: false,
            floatingFilter: false,
            headerComponentFramework: FilterHeader,
            headerComponentParams: { columnId: "createdAt" },
            cellStyle: {
              padding: 0,
            },

            cellRenderer: (params: { value?: string | null }) => (
              <TextToolTip style={{ padding: 0, textAlign: "center" }}>
                {params.value ?? ""}
              </TextToolTip>
            ),
          },
        ],
      },
    ];
  }, [
    FilterHeader,
    actionsColWidth,
    clearInlineEditorMessages,
    closeInlineEditor,
    companyId,
    currentWorkerId,
    currentPage,
    detailBase,
    editingDetail,
    editingRowId,
    editorError,
    editorLoading,
    editorSavedMessage,
    editorSaving,
    displaySize,
    fontSize,
    height,
    isEditMode,
    loadInlineEditor,
    monthlyMaxBreakPairs,
    navigate,
    pageSize,
    rowData.length,
    saveInlineEditor,
  ]);

  const getRowIdForForm = useCallback(
    (data: { id?: string | number }) => data.id,
    [],
  );

  useEffect(() => {
    closeInlineEditor();
  }, [month, year, currentPage]);

  return {
    ...props,
    getRowIdForForm,
    gridRef,
    gridApi,
    rowData,
    columnDefs,
    defaultColDef: {
      floatingFilter: false,
    },
    agLocaleText: TRACKING_LIST_AG_LOCALE,
    loading,
    page: currentPage,
    total: monthlyMeta?.totalRecords ?? monthlyRecords.length,
    pageSize,
    setPage: (p: number) => onChangePagination?.(p, pageSize),
    setPageSize: (size: number) => onChangePagination?.(1, size),
    onGridReady,
    onFilterUpdate: undefined,
    clearFilter: undefined,
    noFitColumns: false,
    suppressMenuHide: false,
    suppressMovableColumns: true,
    floatingFiltersHeight: 0,
    tableHeight,
    rowHeight: height,
    getRowHeight: (params: { data?: TrackingAgRow }) =>
      params.data?.id === editingRowId ? expandedRowHeight : height,
    headerHeight: height + 2,
    isShowBtnCreate: false,
    isShowButtonEnd: false,
    isShowPaginationHeader: true,
    disabledPagination: isEditMode,
    isEditMode,
    tableFormRef,
    handleSetStateForm,
    preHeaderChildren,
    filterInfo,
    setFilterInfo,
    filter,
    filterData,
    selectedFilterOptions,
    selectedFilterType,
    handleSetFilter,
    isFetchingFilterOptions,
    hideSpecialEmptyCheckbox: true,
    displaySize,
  };
};

export type Props = ReturnType<typeof useTableList>;

export default useTableList;
